import React, { useState, useEffect } from 'react'
import { Alert, Platform, TextInput, RefreshControl } from 'react-native'
import {
  ArrowLeft, Users, UserPlus, Shield, ShieldCheck, ShieldAlert,
  X, Check, Trash2, Mail, ChevronDown, Crown,
} from 'lucide-react-native'
import { useLanguage } from '@fliisi/i18n'
import { useAuth } from '../contexts/AuthContext'
import { useToast } from '../components/Toast'
import { supabase } from '../lib/supabase'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import {
  Button, ButtonText, Dialog, Spinner, Text,
  XStack, YStack, ScrollView,
} from '@fliisi/pollen'
import { FullScreenLayout } from '../components/ScreenLayout'

const merchantColors = { primary: '#C07A5F', primaryLight: '#D4A088', accent: '#748C70' }
type StaffRole = 'cashier' | 'admin' | 'super_admin'

interface StaffMember {
  id: string; user_id: string; role: StaffRole; is_owner: boolean; created_at: string
  deactivated_at: string | null; user_email: string | null; user_name: string | null
}
interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'MerchantStaff'>
  route: RouteProp<RootStackParamList, 'MerchantStaff'>
}

const ROLE_CONFIG: Record<StaffRole, { label: string; labelFi: string; color: string; icon: typeof Shield; desc: string; descFi: string }> = {
  cashier: { label: 'Cashier', labelFi: 'Kassatyöntekijä', color: '#3B82F6', icon: Shield, desc: 'Can process sales and view inventory', descFi: 'Voi käsitellä myyntejä ja nähdä varaston' },
  admin: { label: 'Admin', labelFi: 'Ylläpitäjä', color: '#F59E0B', icon: ShieldCheck, desc: 'Can manage inventory, staff, and settings', descFi: 'Voi hallita varastoa, henkilökuntaa ja asetuksia' },
  super_admin: { label: 'Owner', labelFi: 'Omistaja', color: merchantColors.primary, icon: ShieldAlert, desc: 'Full access to everything', descFi: 'Täysi pääsy kaikkeen' },
}

export default function MerchantStaffScreen({ navigation, route }: Props) {
  const { storeId } = route.params
  const { t, language } = useLanguage()
  const { user } = useAuth()
  const toast = useToast()

  const [staff, setStaff] = useState<StaffMember[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [showInvite, setShowInvite] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<StaffRole>('cashier')
  const [inviting, setInviting] = useState(false)
  const [showRoleModal, setShowRoleModal] = useState(false)
  const [editingMember, setEditingMember] = useState<StaffMember | null>(null)
  const [myRole, setMyRole] = useState<StaffRole | null>(null)
  const [isOwner, setIsOwner] = useState(false)

  useEffect(() => { fetchStaff() }, [storeId])

  const fetchStaff = async () => {
    try {
      const { data, error } = await supabase.from('flea_market_staff')
        .select('id, user_id, role, is_owner, created_at, deactivated_at, user:users ( email, full_name )')
        .eq('flea_market_id', storeId).is('deactivated_at', null)
        .order('is_owner', { ascending: false }).order('role', { ascending: false }).order('created_at', { ascending: true })
      if (error) throw error
      const members: StaffMember[] = (data || []).map((s: any) => {
        const u = Array.isArray(s.user) ? s.user[0] : s.user
        return { id: s.id, user_id: s.user_id, role: s.role, is_owner: s.is_owner, created_at: s.created_at, deactivated_at: s.deactivated_at, user_email: u?.email || null, user_name: u?.full_name || null }
      })
      setStaff(members)
      const me = members.find(m => m.user_id === user?.id)
      if (me) { setMyRole(me.role); setIsOwner(me.is_owner) }
    } catch (error) { console.error('Error fetching staff:', error) }
    finally { setLoading(false); setRefreshing(false) }
  }

  const canManage = myRole === 'admin' || myRole === 'super_admin' || isOwner

  const inviteStaff = async () => {
    if (!inviteEmail.trim() || !inviteEmail.includes('@')) { toast.error(t('error') || 'Error', t('enter_valid_email') || 'Enter a valid email'); return }
    setInviting(true)
    try {
      const { data: targetUser, error: lookupError } = await supabase.from('users').select('id, email').eq('email', inviteEmail.trim().toLowerCase()).maybeSingle()
      if (lookupError) throw lookupError
      if (!targetUser) { toast.error(t('user_not_found') || 'User not found', t('user_must_register') || 'They need a Fliisi account first'); return }
      if (staff.find(s => s.user_id === targetUser.id)) { toast.error(t('already_staff') || 'Already staff', t('user_already_member') || 'This user is already a team member'); return }
      const { error: insertError } = await supabase.from('flea_market_staff').insert({ flea_market_id: storeId, user_id: targetUser.id, role: inviteRole, is_owner: false, invited_by: user?.id })
      if (insertError) throw insertError
      toast.success(t('staff_added') || 'Staff added', `${targetUser.email} → ${language === 'fi' ? ROLE_CONFIG[inviteRole].labelFi : ROLE_CONFIG[inviteRole].label}`)
      setShowInvite(false); setInviteEmail(''); setInviteRole('cashier'); fetchStaff()
    } catch (error: any) { console.error('Error inviting staff:', error); toast.error(t('error') || 'Error', error.message) }
    finally { setInviting(false) }
  }

  const changeRole = async (memberId: string, newRole: StaffRole) => {
    try {
      const { error } = await supabase.from('flea_market_staff').update({ role: newRole }).eq('id', memberId)
      if (error) throw error
      setStaff(prev => prev.map(s => s.id === memberId ? { ...s, role: newRole } : s))
      toast.success(t('role_updated') || 'Role updated', ''); setShowRoleModal(false); setEditingMember(null)
    } catch (error: any) { toast.error(t('error') || 'Error', error.message) }
  }

  const removeStaff = async (member: StaffMember) => {
    if (member.is_owner) { toast.error(t('error') || 'Error', t('cannot_remove_owner') || 'Cannot remove the owner'); return }
    const doRemove = async () => {
      try {
        const { error } = await supabase.from('flea_market_staff').update({ deactivated_at: new Date().toISOString() }).eq('id', member.id)
        if (error) throw error
        setStaff(prev => prev.filter(s => s.id !== member.id)); toast.success(t('staff_removed') || 'Staff removed', '')
      } catch (error: any) { toast.error(t('error') || 'Error', error.message) }
    }
    if (Platform.OS === 'web') { if (window.confirm(`${t('remove') || 'Remove'} ${member.user_email || member.user_name}?`)) doRemove() }
    else Alert.alert(t('remove_staff') || 'Remove staff', `${t('remove') || 'Remove'} ${member.user_email || member.user_name}?`, [{ text: t('cancel') || 'Cancel', style: 'cancel' }, { text: t('remove') || 'Remove', style: 'destructive', onPress: doRemove }])
  }

  const getRoleLabel = (role: StaffRole) => language === 'fi' ? ROLE_CONFIG[role].labelFi : ROLE_CONFIG[role].label
  const getRoleDesc = (role: StaffRole) => language === 'fi' ? ROLE_CONFIG[role].descFi : ROLE_CONFIG[role].desc
  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString(language === 'fi' ? 'fi-FI' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' })

  return (
    <FullScreenLayout>
      {/* Header */}
      <XStack ai="center" jc="space-between" px="$md" pt="$md" pb="$sm" bg="$backgroundCard" bbw={1} bbc="$borderColor">
        <YStack p={4} onPress={() => navigation.goBack()} pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer">
          <ArrowLeft size={20} color={merchantColors.primary} />
        </YStack>
        <YStack ai="center" f={1}>
          <Text fontSize={17} fontWeight="700" color="$color">{t('staff') || 'Staff'}</Text>
          <Text fontSize={12} color="$colorSecondary" mt={1}>{staff.length} {staff.length === 1 ? (t('member') || 'member') : (t('members') || 'members')}</Text>
        </YStack>
        {canManage ? (
          <YStack w={40} h={40} br="$full" bg={merchantColors.primary} jc="center" ai="center" onPress={() => setShowInvite(true)} pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer">
            <UserPlus size={20} color="#fff" />
          </YStack>
        ) : <YStack w={40} />}
      </XStack>

      {/* Staff list */}
      {loading ? <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack> : (
        <ScrollView f={1} contentContainerStyle={{ padding: 16, paddingBottom: 100, gap: 10 }} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); fetchStaff() }} colors={[merchantColors.primary]} />}>
          {staff.length === 0 ? (
            <YStack ai="center" py="$3xl" px="$2xl">
              <Users size={48} color="#ddd" />
              <Text fontSize={17} fontWeight="600" color="$colorSecondary" mt="$md" mb="$sm">{t('no_staff') || 'No staff yet'}</Text>
              <Text fontSize={14} color="$colorSecondary" ta="center">{t('invite_staff_hint') || 'Invite team members to help manage your store'}</Text>
            </YStack>
          ) : staff.map(item => {
            const config = ROLE_CONFIG[item.role]
            const IconComponent = config.icon
            const isMe = item.user_id === user?.id
            const displayName = item.user_name || item.user_email || 'Unknown'
            return (
              <XStack key={item.id} ai="center" bg="$backgroundCard" br={10} p={14} gap="$sm" elevation={2}>
                <YStack w={44} h={44} br={22} bg={`${config.color}18`} jc="center" ai="center">
                  {item.is_owner ? <Crown size={20} color={config.color} /> : <IconComponent size={20} color={config.color} />}
                </YStack>
                <YStack f={1} minWidth={0}>
                  <Text fontSize={15} fontWeight="600" color="$color" numberOfLines={1}>
                    {displayName}{isMe && <Text fontSize={12} fontWeight="500" color="$colorSecondary"> ({t('you') || 'you'})</Text>}
                  </Text>
                  {item.user_email && item.user_name && <Text fontSize={12} color="$colorSecondary" mt={1} numberOfLines={1}>{item.user_email}</Text>}
                  <XStack ai="center" gap="$sm" mt={6}>
                    <YStack px="$sm" py={3} br={6} bg={`${config.color}18`}><Text fontSize={11} fontWeight="700" color={config.color}>{getRoleLabel(item.role)}</Text></YStack>
                    <Text fontSize={11} color="#bbb">{formatDate(item.created_at)}</Text>
                  </XStack>
                </YStack>
                {canManage && !isMe && !item.is_owner && (
                  <XStack gap={4}>
                    <YStack w={34} h={34} br={17} bg="$backgroundSecondary" jc="center" ai="center" onPress={() => { setEditingMember(item); setShowRoleModal(true) }} pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer">
                      <ChevronDown size={16} color={merchantColors.primary} />
                    </YStack>
                    <YStack w={34} h={34} br={17} bg="$backgroundSecondary" jc="center" ai="center" onPress={() => removeStaff(item)} pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer">
                      <Trash2 size={16} color="#EF4444" />
                    </YStack>
                  </XStack>
                )}
              </XStack>
            )
          })}
        </ScrollView>
      )}

      {/* Invite Modal */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
          <YStack p="$xl">
            <XStack ai="center" jc="space-between" mb={20}>
              <Text fontSize={18} fontWeight="700" color="$color">{t('invite_staff') || 'Invite staff'}</Text>
              <YStack onPress={() => setShowInvite(false)} pressStyle={{ opacity: 0.8 }} animation="quick" cursor="pointer"><X size={22} color="#999" /></YStack>
            </XStack>

            <Text fontSize={13} fontWeight="600" color="$colorSecondary" mb={6} mt="$sm">{t('email') || 'Email'}</Text>
            <XStack ai="center" gap={10} bg="$backgroundSecondary" br="$md" px="$sm" py={10}>
              <Mail size={18} color="#999" />
              <TextInput style={{ flex: 1, fontSize: 15, color: '#1a1a1a' }} value={inviteEmail} onChangeText={setInviteEmail} placeholder={t('staff_email_placeholder') || 'name@example.com'} placeholderTextColor="#bbb" keyboardType="email-address" autoCapitalize="none" autoCorrect={false} />
            </XStack>

            <Text fontSize={13} fontWeight="600" color="$colorSecondary" mb={6} mt="$sm">{t('role') || 'Role'}</Text>
            <YStack gap="$sm">
              {(['cashier', 'admin'] as StaffRole[]).map(role => {
                const config = ROLE_CONFIG[role]; const Icon = config.icon; const selected = inviteRole === role
                return (
                  <XStack key={role} ai="center" gap="$sm" p="$sm" br="$md" bw={1.5} boc={selected ? config.color : '$borderColor'} bg={selected ? `${config.color}08` : 'transparent'}
                    onPress={() => setInviteRole(role)} pressStyle={{ opacity: 0.8 }} animation="quick" cursor="pointer">
                    <Icon size={20} color={selected ? config.color : '#ccc'} />
                    <YStack f={1}>
                      <Text fontSize={14} fontWeight="600" color={selected ? config.color : '$color'}>{getRoleLabel(role)}</Text>
                      <Text fontSize={12} color="$colorSecondary" mt={2}>{getRoleDesc(role)}</Text>
                    </YStack>
                    {selected && <Check size={18} color={config.color} />}
                  </XStack>
                )
              })}
            </YStack>

            <XStack jc="center" ai="center" gap="$sm" bg={merchantColors.primary} br="$md" py={14} mt={20} opacity={inviting ? 0.6 : 1}
              onPress={inviting ? undefined : inviteStaff} pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer">
              {inviting ? <Spinner size="small" /> : <><UserPlus size={18} color="#fff" /><Text fontSize={16} fontWeight="700" color="white">{t('invite') || 'Invite'}</Text></>}
            </XStack>
          </YStack>
      </Dialog>

      {/* Role Change Modal */}
      <Dialog open={showRoleModal} onOpenChange={setShowRoleModal}>
          <YStack p="$xl">
            <Text fontSize={18} fontWeight="700" color="$color">{t('change_role') || 'Change role'}</Text>
            <Text fontSize={14} color="$colorSecondary" mt={4} mb="$md">{editingMember?.user_name || editingMember?.user_email}</Text>
            <YStack gap="$sm">
              {(['cashier', 'admin'] as StaffRole[]).map(role => {
                const config = ROLE_CONFIG[role]; const Icon = config.icon; const isCurrent = editingMember?.role === role
                return (
                  <XStack key={role} ai="center" gap="$sm" p={14} br="$md" bg={isCurrent ? `${merchantColors.primary}08` : '$backgroundSecondary'} bw={isCurrent ? 1 : 0} boc={merchantColors.primaryLight}
                    onPress={() => editingMember && changeRole(editingMember.id, role)} pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer">
                    <Icon size={20} color={config.color} />
                    <Text f={1} fontSize={15} fontWeight="600" color="$color">{getRoleLabel(role)}</Text>
                    {isCurrent && <Text fontSize={12} color="$colorSecondary" fontStyle="italic">{t('current') || 'current'}</Text>}
                  </XStack>
                )
              })}
            </YStack>
          </YStack>
      </Dialog>
    </FullScreenLayout>
  )
}
